<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanCard from './KanbanCard.vue';
import { formatMonetaryValue, parseMonetaryValue } from './helpers';

const props = defineProps({
  label: {
    type: Object,
    required: true,
    // { id, title, color }
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  visibleFields: {
    type: Array,
    default: () => [],
  },
  monetaryAttributeKey: {
    type: String,
    default: '',
  },
  monetaryAttributeLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['drop', 'dragover']);

const { t } = useI18n();

const isDragOver = ref(false);
const draggedConversationId = ref(null);
const dropIndex = ref(null);

const conversationCount = computed(() => props.conversations.length);
const columnMonetaryTotal = computed(() => {
  if (!props.monetaryAttributeKey) {
    return '';
  }

  const total = props.conversations.reduce((sum, conversation) => {
    const value =
      conversation.custom_attributes?.[props.monetaryAttributeKey] ||
      conversation.customAttributes?.[props.monetaryAttributeKey];

    return sum + parseMonetaryValue(value);
  }, 0);

  return formatMonetaryValue(total);
});

const handleDragOver = e => {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  isDragOver.value = true;
  dropIndex.value = props.conversations.length;
  emit('dragover', props.label.id);
};

const handleDragLeave = () => {
  isDragOver.value = false;
  dropIndex.value = null;
};

const handleDrop = e => {
  e.preventDefault();
  isDragOver.value = false;
  const targetIndex =
    dropIndex.value === null ? props.conversations.length : dropIndex.value;

  const conversationId = parseInt(e.dataTransfer.getData('text/plain'), 10);
  const fromLabelId = e.dataTransfer.getData('kanban/fromLabelId');
  const fromIndex = Number.parseInt(
    e.dataTransfer.getData('kanban/fromIndex'),
    10
  );

  if (conversationId) {
    emit('drop', {
      conversationId,
      fromLabelId: fromLabelId || undefined,
      fromIndex: Number.isNaN(fromIndex) ? undefined : fromIndex,
      toIndex: targetIndex,
      labelId: props.label.id,
      labelTitle: props.label.title,
    });
  }

  dropIndex.value = null;
};

const handleDragStart = (conversationId, index) => {
  draggedConversationId.value = conversationId;
  dropIndex.value = index;
};

const handleDragEnd = () => {
  draggedConversationId.value = null;
  dropIndex.value = null;
};

const handleCardDragOver = index => {
  dropIndex.value = index;
  isDragOver.value = true;
};
</script>

<template>
  <div
    class="flex w-80 flex-col overflow-hidden rounded-xl border border-n-weak bg-white shadow-sm dark:bg-n-solid-2"
    :class="{ 'ring-2 ring-offset-2': isDragOver }"
    :style="{ '--ring-color': label.color }"
  >
    <!-- Header da coluna -->
    <div
      class="border-b border-n-weak px-4 py-3"
      :style="{ backgroundColor: label.color + '15' }"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div
            class="w-3 h-3 rounded-full flex-shrink-0"
            :style="{ backgroundColor: label.color }"
          />
          <h3
            class="text-sm font-semibold uppercase tracking-wide text-n-slate-12"
          >
            {{ label.title }}
          </h3>
        </div>
        <div class="flex items-center gap-2">
          <span
            v-if="columnMonetaryTotal"
            class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
          >
            {{ columnMonetaryTotal }}
          </span>
          <span
            class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
          >
            {{ conversationCount }}
          </span>
        </div>
      </div>
    </div>

    <!-- Área de drop para cards -->
    <div
      class="min-h-96 flex-1 space-y-2 overflow-y-auto bg-[#f7f8fb] p-3 transition-colors dark:bg-n-background"
      :class="[
        isDragOver
          ? 'border-2 border-dashed bg-n-alpha-1'
          : 'border-2 border-transparent',
      ]"
      :style="isDragOver ? { borderColor: label.color + '50' } : {}"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <!-- Cards de conversa -->
      <template v-if="conversations.length > 0">
        <KanbanCard
          v-for="(conversation, index) in conversations"
          :key="conversation.id"
          :conversation="conversation"
          :label-id="label.id"
          :index="index"
          :is-dragging="draggedConversationId === conversation.id"
          :visible-fields="visibleFields"
          :monetary-attribute-key="monetaryAttributeKey"
          :monetary-attribute-label="monetaryAttributeLabel"
          @dragstart="handleDragStart(conversation.id, index)"
          @dragend="handleDragEnd"
          @dragover.prevent="handleCardDragOver(index)"
        />
      </template>

      <!-- Estado vazio -->
      <div
        v-else
        class="flex h-full items-center justify-center text-center text-n-slate-10"
      >
        <div class="py-8">
          <div class="i-lucide-inbox size-8 mx-auto mb-2 opacity-50" />
          <p class="text-sm">
            {{ t('KANBAN.EMPTY_COLUMN') || 'Nenhuma conversa' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
